import { useStore } from '../lib/store.ts'
import { moduleProgress } from '../lib/course.ts'
import { Bar, NoteView, PhraseChips, Speak, TopBar } from '../components/ui.tsx'
import { navigate } from '../lib/router.ts'
import { useState } from 'react'

export function Modules() {
  const { idx, cards } = useStore()
  const introduced = (id: string) => (cards.get(id)?.state ?? 'new') !== 'new'

  return (
    <div className="screen stack">
      <h1 style={{ paddingTop: 8 }}>Модулі курсу</h1>
      <p className="muted small">
        Кожен модуль — це ситуація, а не граматична тема. Всередині: фрази, діалог,
        рольова сцена і пояснення.
      </p>
      <div className="list">
        {idx.modules.map((m) => {
          const p = moduleProgress(idx, m.id, introduced)
          return (
            <a className="item" key={m.id} href={`#/module/${m.id}`}>
              <span className="item__emoji" aria-hidden>{m.emoji}</span>
              <span className="item__body">
                <span className="item__title">{m.title}</span>
                <span className="item__sub">{p.done} з {p.total} фраз</span>
                <span style={{ display: 'block', marginTop: 6 }}>
                  <Bar value={p.done} max={p.total} good={p.done === p.total} />
                </span>
              </span>
            </a>
          )
        })}
      </div>
    </div>
  )
}

export function ModuleView({ id }: { id: string }) {
  const { idx, cards } = useStore()
  const m = idx.moduleById.get(id)
  const [openPhrases, setOpenPhrases] = useState(false)
  if (!m) return <div className="screen">Модуль не знайдено</div>

  const introduced = (pid: string) => (cards.get(pid)?.state ?? 'new') !== 'new'
  const p = moduleProgress(idx, m.id, introduced)

  return (
    <>
      <TopBar title={m.title} />
      <div className="screen stack">
        <div className="card stack--tight stack">
          <div className="row">
            <span style={{ fontSize: 30 }} aria-hidden>{m.emoji}</span>
            <div>
              <div style={{ fontWeight: 600 }}>Після цього модуля</div>
              <p className="small muted">{m.goal}</p>
            </div>
          </div>
          <Bar value={p.done} max={p.total} good={p.done === p.total} />
          <div className="tiny faint">{p.done} з {p.total} фраз уже в роботі</div>
          <button className="btn btn--primary btn--wide" onClick={() => navigate(`/session/module/${m.id}`)}>
            Тренувати модуль
          </button>
        </div>

        {m.id === 'zvuky' && (
          <a className="item" href="#/pairs">
            <span className="item__emoji" aria-hidden>👂</span>
            <span className="item__body">
              <span className="item__title">Тренажер розрізнення звуків</span>
              <span className="item__sub">21 пара справжніх слів на ł/l, sz/ś, cz/ć, носові, y/i, ó/o</span>
            </span>
            <span className="item__after">→</span>
          </a>
        )}

        {m.insights.length > 0 && (
          <section className="stack--tight stack">
            <h3>Як це працює</h3>
            <div className="list">
              {m.insights.map((ins) => (
                <a className="item" key={ins.id} href={`#/insight/${m.id}/${ins.id}`}>
                  <span className="item__emoji" aria-hidden>💡</span>
                  <span className="item__body"><span className="item__title">{ins.title}</span></span>
                  <span className="item__after">→</span>
                </a>
              ))}
            </div>
          </section>
        )}

        {m.dialogues.length > 0 && (
          <section className="stack--tight stack">
            <h3>Діалоги</h3>
            <div className="list">
              {m.dialogues.map((d) => (
                <a className="item" key={d.id} href={`#/dialogue/${m.id}/${d.id}`}>
                  <span className="item__emoji" aria-hidden>💬</span>
                  <span className="item__body">
                    <span className="item__title">{d.title}</span>
                    <span className="item__sub">{d.lines.length} реплік</span>
                  </span>
                  <span className="item__after">→</span>
                </a>
              ))}
            </div>
          </section>
        )}

        {m.roleplays.length > 0 && (
          <section className="stack--tight stack">
            <h3>Рольова сцена</h3>
            <div className="list">
              {m.roleplays.map((r) => (
                <a className="item" key={r.id} href={`#/roleplay/${m.id}/${r.id}`}>
                  <span className="item__emoji" aria-hidden>🎭</span>
                  <span className="item__body">
                    <span className="item__title">{r.title}</span>
                    <span className="item__sub">{r.role}</span>
                  </span>
                  <span className="item__after">→</span>
                </a>
              ))}
            </div>
          </section>
        )}

        <section className="stack--tight stack">
          <button className="item" onClick={() => setOpenPhrases((v) => !v)}>
            <span className="item__emoji" aria-hidden>📖</span>
            <span className="item__body">
              <span className="item__title">Усі фрази модуля</span>
              <span className="item__sub">{m.phrases.length} штук</span>
            </span>
            <span className="item__after">{openPhrases ? '▲' : '▼'}</span>
          </button>
          {openPhrases && (
            <div className="stack--tight stack">
              {m.phrases.map((ph) => (
                <div className="card" key={ph.id}>
                  <div className="row row--between" style={{ alignItems: 'flex-start' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 570 }}>{ph.pl}</div>
                      <div className="small muted">{ph.uk}</div>
                      {ph.tr && <div className="tiny" style={{ color: 'var(--accent)' }}>{ph.tr}</div>}
                    </div>
                    <Speak audio={ph.audio} text={ph.pl} small />
                  </div>
                  <div style={{ marginTop: 8 }}><PhraseChips phrase={ph} /></div>
                  {(ph.notes ?? []).map((n, i) => (
                    <div key={i} style={{ marginTop: 8 }}><NoteView note={n} /></div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </>
  )
}
